var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../config';
import { TABLE_HIERARCHY_COLORS } from '../../../enum';
import { TableCell, TableRow } from '../../common';
import { ExpandableRowLabel } from '../../common/tableHierarchy';
import { useCurrencySymbol } from '../../hooks/useCurrencyOptions';
import { QueueType } from '../../multiskill/types';
import { useResourcingContext } from '../../report/hooks';
import { DisplayMode } from '../../report/resourcing';
import { useDisplayModeParams } from '../../report/selectors';
import { PLAN_COLORS } from '../../wizard/constants';
import { FirstCell, STICKIES } from '../annualTable';
import { LobHeaderRow } from '../common/lobHeaderRow';
import { getMultiskillCategoryExpansionKey, getMultiskillGroupCountryExpansionKey, getMultiskillGroupExpansionKey, useControlledExpansion, useMultiskillGroupNames } from '../common/selectors';
import { getChunkBaselineValue, getExpandChildren, getHighlightBorder, getScenarioCellColor } from '../common/utils';
import { HIERARCHY_LEVEL, MULTISKILL_COLOR, NEST_LEVEL, TOTAL_ROW_COLOR } from '../constants';
import { generateVariances } from '../selectors';
import { SupportStaffCountryRow, SupportStaffLobQueueRows } from './supportStaffDetails';
export var getSupportStaffLobExpansionKey = function (roleName, lobName) {
  return "".concat(roleName, ":lob:").concat(lobName);
};
var emptySupportStaffQueuesByLob = function (lobName) {
  return {
    lobName: lobName,
    defQueues: [],
    nonDefQueues: []
  };
};
var mergeGroupCountries = function (data, name) {
  var byName = new Map();
  data.forEach(function (scenario) {
    var _a, _b;
    var group = (_a = scenario.multiSkill) === null || _a === void 0 ? void 0 : _a.find(function (g) {
      return g.name === name;
    });
    (_b = group === null || group === void 0 ? void 0 : group.countries) === null || _b === void 0 ? void 0 : _b.forEach(function (country) {
      var existing = byName.get(country.countryName);
      if (!existing) {
        byName.set(country.countryName, __assign(__assign({}, country), {
          locations: __spreadArray([], country.locations, true)
        }));
        return;
      }
      var existingNames = new Set(existing.locations.map(function (l) {
        return l.locationName;
      }));
      country.locations.forEach(function (location) {
        if (!existingNames.has(location.locationName)) {
          existing.locations.push(__assign({}, location));
          existingNames.add(location.locationName);
        }
      });
    });
  });
  return Array.from(byName.values());
};
/** One row per multiskill group showing the group's support staff for this
 * role, so the role total (which already includes the groups) matches the
 * sum of the visible rows. */
var SupportStaffGroupRows = function (_a) {
  var _b;
  var roleName = _a.roleName,
    data = _a.data,
    chunkSize = _a.chunkSize,
    valueKey = _a.valueKey,
    decimal = _a.decimal,
    currencySymbol = _a.currencySymbol,
    groupNames = _a.groupNames,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem;
  var t = useTranslation('report').t;
  var isPercentages = useResourcingContext().isPercentages;
  var displayMode = useDisplayModeParams()[0];
  var _c = useCurrencySymbol(currencySymbol),
    currencyPrefix = _c.prefix,
    currencySuffix = _c.suffix;
  var varianceOptions = {
    decimalPlaces: currencySymbol ? 0 : undefined,
    currencyPrefix: currencyPrefix,
    currencySuffix: currencySuffix
  };
  if (!groupNames.length) return null;
  var categoryExpansionKey = getMultiskillCategoryExpansionKey(roleName);
  var categoryExpanded = onToggleItem ? (_b = expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[categoryExpansionKey]) !== null && _b !== void 0 ? _b : false : true;
  var roleField = valueKey === 'supportStaffSalaries' ? 'staffSalaries' : 'supportStaff';
  var getRoleValue = function (entries) {
    var _a, _b;
    return (_b = (_a = entries === null || entries === void 0 ? void 0 : entries.find(function (entry) {
      return entry.role === roleName;
    })) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : null;
  };
  var groupRoleValue = function (scenario, name) {
    var _a, _b;
    return getRoleValue((_b = (_a = scenario.multiSkill) === null || _a === void 0 ? void 0 : _a.find(function (g) {
      return g.name === name;
    })) === null || _b === void 0 ? void 0 : _b[roleField]);
  };
  var locationRoleValue = function (scenario, name, countryName, locationName) {
    var _a, _b, _c, _d, _e;
    var location = (_e = (_d = (_c = (_b = (_a = scenario.multiSkill) === null || _a === void 0 ? void 0 : _a.find(function (g) {
      return g.name === name;
    })) === null || _b === void 0 ? void 0 : _b.countries) === null || _c === void 0 ? void 0 : _c.find(function (c) {
      return c.countryName === countryName;
    })) === null || _d === void 0 ? void 0 : _d.locations) === null || _e === void 0 ? void 0 : _e.find(function (l) {
      return l.locationName === locationName;
    });
    return getRoleValue(location === null || location === void 0 ? void 0 : location[roleField]);
  };
  return _jsxs(_Fragment, {
    children: [_jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        "aria-label": NEST_LEVEL[HIERARCHY_LEVEL.supportStaffLob],
        stickySizes: STICKIES,
        color: TABLE_HIERARCHY_COLORS.lightBlueGray
      }, {
        children: _jsx(Box, __assign({
          fontWeight: "bold"
        }, {
          children: _jsx(ExpandableRowLabel, __assign({
            expanded: categoryExpanded,
            onToggle: onToggleItem ? function () {
              return onToggleItem(categoryExpansionKey);
            } : undefined,
            bold: true
          }, {
            children: t('annual.reportRows.multiSkill')
          }))
        }))
      })), data.map(function (_, index, arr) {
        return _jsx(TableCell, {
          colSpan: 2,
          color: TABLE_HIERARCHY_COLORS.lightBlueGray,
          highlightBorder: getHighlightBorder(index, arr.length, chunkSize)
        }, "".concat(roleName, "-multiskill-cat-").concat(index));
      })]
    }), categoryExpanded && groupNames.map(function (name) {
      var _a;
      var scenarioValues = data.map(function (scenario) {
        return groupRoleValue(scenario, name);
      });
      var countries = mergeGroupCountries(data, name);
      var groupExpansionKey = getMultiskillGroupExpansionKey(name, roleName);
      var expanded = onToggleItem ? (_a = expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[groupExpansionKey]) !== null && _a !== void 0 ? _a : false : true;
      var hasCountries = !!countries.length;
      return _jsxs(React.Fragment, {
        children: [_jsxs(TableRow, {
          children: [_jsx(FirstCell, __assign({
            "aria-label": NEST_LEVEL[HIERARCHY_LEVEL.supportStaffQueue],
            stickySizes: STICKIES,
            bold: true,
            sx: {
              backgroundColor: MULTISKILL_COLOR
            }
          }, {
            children: _jsx(ExpandableRowLabel, __assign({
              expanded: expanded,
              onToggle: hasCountries && onToggleItem ? function () {
                return onToggleItem(groupExpansionKey);
              } : undefined,
              bold: true
            }, {
              children: name
            }))
          })), scenarioValues.map(function (value, index, arr) {
            var _a, _b;
            var scenario = data[index];
            var baseValue = getChunkBaselineValue(scenarioValues, index, chunkSize);
            var isGroupedMultiskillScenario = scenario.dataType === QueueType.Multiskill && !!((_a = scenario.multiSkill) === null || _a === void 0 ? void 0 : _a.some(function (g) {
              return g.name === name;
            }));
            return _jsx(TableCell, {
              colSpan: 2,
              value: value,
              decimal: decimal,
              currency: currencySymbol,
              bold: true,
              color: isGroupedMultiskillScenario ? MULTISKILL_COLOR : (_b = PLAN_COLORS[scenario.colorIndex]) === null || _b === void 0 ? void 0 : _b[1],
              useColorValue: isGroupedMultiskillScenario,
              highlightBorder: getHighlightBorder(index, arr.length, chunkSize),
              expandchildren: displayMode === DisplayMode.All ? undefined : generateVariances(baseValue !== null && baseValue !== void 0 ? baseValue : null, value, isPercentages, varianceOptions)
            }, "".concat(roleName, "-group-").concat(name, "-").concat(index));
          })]
        }), expanded && countries.map(function (country) {
          var countryExpansionKey = getMultiskillGroupCountryExpansionKey(name, country.countryName, roleName);
          return _jsx(SupportStaffCountryRow, {
            country: {
              countryName: country.countryName,
              value: 0,
              locations: country.locations.map(function (location) {
                return {
                  locationName: location.locationName,
                  value: 0
                };
              })
            },
            scenarioValues: data.map(function (scenario) {
              var values = country.locations.map(function (location) {
                return locationRoleValue(scenario, name, country.countryName, location.locationName);
              });
              return values.some(function (v) {
                return v !== null;
              }) ? values.reduce(function (a, b) {
                return (a !== null && a !== void 0 ? a : 0) + (b !== null && b !== void 0 ? b : 0);
              }, 0) : null;
            }),
            scenarioLocationValues: country.locations.map(function (location) {
              return data.map(function (scenario) {
                return locationRoleValue(scenario, name, country.countryName, location.locationName);
              });
            }),
            data: data,
            chunkSize: chunkSize,
            expandedProp: expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[countryExpansionKey],
            onToggle: onToggleItem ? function () {
              return onToggleItem(countryExpansionKey);
            } : undefined,
            decimal: decimal,
            currencySymbol: currencySymbol,
            isMultiskillGroup: true,
            groupName: name
          }, "".concat(roleName, "-group-").concat(name, "-").concat(country.countryName));
        })]
      }, name);
    })]
  });
};
var SupportStaffLobRows = function (_a) {
  var roleName = _a.roleName,
    lob = _a.lob,
    lobIdx = _a.lobIdx,
    perScenarioLob = _a.perScenarioLob,
    data = _a.data,
    chunkSize = _a.chunkSize,
    valueKey = _a.valueKey,
    decimal = _a.decimal,
    currencySymbol = _a.currencySymbol,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem;
  var t = useTranslation('report', {
    keyPrefix: 'annual'
  }).t;
  var expansionKey = getSupportStaffLobExpansionKey(roleName, lob.lobName);
  var _b = useControlledExpansion(expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[expansionKey], onToggleItem ? function () {
      return onToggleItem(expansionKey);
    } : undefined),
    expanded = _b.expanded,
    handleToggle = _b.handleToggle;
  return _jsxs(React.Fragment, {
    children: [_jsx(LobHeaderRow, {
      displayName: lob.lobName || t('unassignedLob'),
      data: data,
      chunkSize: chunkSize,
      backgroundColor: theme.palette.grey[400],
      expanded: expanded,
      onToggle: handleToggle,
      nestLevel: HIERARCHY_LEVEL.supportStaffLob
    }), expanded && _jsx(SupportStaffLobQueueRows, {
      lob: lob,
      perScenarioLob: perScenarioLob,
      data: data,
      chunkSize: chunkSize,
      expansionKeyPrefix: expansionKey,
      expandedItems: expandedItems,
      onToggleItem: onToggleItem,
      valueKey: valueKey,
      decimal: decimal,
      currencySymbol: currencySymbol
    })]
  }, "".concat(roleName, "-lob-").concat(lob.lobName, "-").concat(lobIdx));
};
export var SupportStaffRoleRow = function (_a) {
  var roleName = _a.roleName,
    scenarioValues = _a.scenarioValues,
    queuesByLob = _a.queuesByLob,
    perScenarioQueues = _a.perScenarioQueues,
    data = _a.data,
    chunkSize = _a.chunkSize,
    expandedProp = _a.expandedProp,
    onToggle = _a.onToggle,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem,
    _b = _a.valueKey,
    valueKey = _b === void 0 ? 'value' : _b,
    _c = _a.decimal,
    decimal = _c === void 0 ? 1 : _c,
    currencySymbol = _a.currencySymbol;
  var isPercentages = useResourcingContext().isPercentages;
  var displayMode = useDisplayModeParams()[0];
  var _d = useCurrencySymbol(currencySymbol),
    currencyPrefix = _d.prefix,
    currencySuffix = _d.suffix;
  var varianceOptions = {
    decimalPlaces: currencySymbol ? 0 : undefined,
    currencyPrefix: currencyPrefix,
    currencySuffix: currencySuffix
  };
  var _e = useControlledExpansion(expandedProp, onToggle),
    expanded = _e.expanded,
    handleToggle = _e.handleToggle;
  var multiskillGroupNames = useMultiskillGroupNames(data);
  var isExpandable = queuesByLob.some(function (lob) {
    return lob.defQueues.length || lob.nonDefQueues.length;
  }) || multiskillGroupNames.length > 0;
  var flatPerScenario = useMemo(function () {
    return perScenarioQueues.flat();
  }, [perScenarioQueues]);
  return _jsxs(_Fragment, {
    children: [_jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        "aria-label": NEST_LEVEL[HIERARCHY_LEVEL.role],
        stickySizes: STICKIES,
        bold: true,
        color: TOTAL_ROW_COLOR
      }, {
        children: _jsx(ExpandableRowLabel, __assign({
          expanded: expanded,
          onToggle: isExpandable ? handleToggle : undefined,
          bold: true
        }, {
          children: roleName
        }))
      })), scenarioValues.map(function (value, index, arr) {
        var baseValue = getChunkBaselineValue(scenarioValues, index, chunkSize);
        return _jsx(TableCell, {
          colSpan: 2,
          value: value,
          decimal: decimal,
          currency: currencySymbol,
          bold: true,
          color: getScenarioCellColor(data[index]),
          highlightBorder: getHighlightBorder(index, arr.length, chunkSize),
          expandchildren: getExpandChildren(displayMode, baseValue !== null && baseValue !== void 0 ? baseValue : null, value, isPercentages, varianceOptions)
        }, "role-".concat(roleName, "-").concat(index));
      })]
    }), expanded && _jsxs(_Fragment, {
      children: [queuesByLob.map(function (lob, lobIdx) {
        var perScenarioLob = flatPerScenario.map(function (scenLobs) {
          var _a;
          return (_a = scenLobs.find(function (scenarioLob) {
            return scenarioLob.lobName === lob.lobName;
          })) !== null && _a !== void 0 ? _a : emptySupportStaffQueuesByLob(lob.lobName);
        });
        return _jsx(SupportStaffLobRows, {
          roleName: roleName,
          lob: lob,
          lobIdx: lobIdx,
          perScenarioLob: perScenarioLob,
          data: data,
          chunkSize: chunkSize,
          valueKey: valueKey,
          decimal: decimal,
          currencySymbol: currencySymbol,
          expandedItems: expandedItems,
          onToggleItem: onToggleItem
        }, "".concat(roleName, "-lob-").concat(lob.lobName, "-").concat(lobIdx));
      }), _jsx(SupportStaffGroupRows, {
        roleName: roleName,
        data: data,
        chunkSize: chunkSize,
        valueKey: valueKey,
        decimal: decimal,
        currencySymbol: currencySymbol,
        groupNames: multiskillGroupNames,
        expandedItems: expandedItems,
        onToggleItem: onToggleItem
      })]
    })]
  });
};